import {
  isSubdomainRoutingEnabled,
  isWorkspacePath,
  toStorefrontUrl,
  toWorkspaceUrl,
  wsPath,
} from './siteHosts';

/** Query key used on `/auth` to resume the pre-login location. */
export const RETURN_URL_PARAM = 'returnUrl';

const LOCAL_STAFF_AUTH_PATH = '/auth/staff';

/**
 * Current path for “resume after login” (relative to this origin).
 * If we are already on an auth screen but carry a nested returnUrl, keep that inner target
 * so session expiry on `/auth?returnUrl=/checkout` does not drop the destination.
 */
export function getReturnPathForAuthRedirect(): string {
  const { pathname, search, hash } = window.location;
  if (pathname === '/auth' || pathname.startsWith('/auth/')) {
    const params = new URLSearchParams(search);
    const raw = params.get(RETURN_URL_PARAM);
    if (raw) {
      const inner = sanitizeReturnPath(raw);
      if (inner) return inner;
    }
    return '/';
  }
  return `${pathname}${search}${hash}`;
}

/** Allow only same-origin relative storefront paths; disallow workspace and auth routes. */
export function sanitizeReturnPath(raw: string): string | null {
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return null;

  let path: string;
  try {
    const u = new URL(trimmed, window.location.origin);
    if (u.origin !== window.location.origin) return null;
    path = `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return null;
  }

  const pathOnly = path.split('?')[0].split('#')[0];
  if (pathOnly === '/auth' || pathOnly.startsWith('/auth/')) return null;
  if (isWorkspacePath(pathOnly)) return null;
  return path;
}

/** Safe return path for staff workspace sign-in (host-native or /workspace/*). */
export function sanitizeStaffReturnPath(raw: string): string | null {
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return null;

  let pathOnly: string;
  let suffix: string;
  try {
    const u = new URL(trimmed, window.location.origin);
    pathOnly = u.pathname;
    suffix = `${u.search}${u.hash}`;
  } catch {
    return null;
  }

  if (pathOnly === '/auth' || pathOnly.startsWith('/auth/')) return null;
  if (!isWorkspacePath(pathOnly)) return null;

  const hostNative = wsPath(pathOnly);
  return `${hostNative}${suffix}`;
}

export function getStaffAuthPath(): string {
  if (isSubdomainRoutingEnabled()) {
    return '/auth';
  }
  return LOCAL_STAFF_AUTH_PATH;
}

/** Absolute or relative URL for staff sign-in on the workspace host. */
export function buildStaffLoginUrl(fromPath: string): string {
  const authPath = getStaffAuthPath();
  const safe = sanitizeStaffReturnPath(fromPath);
  const query = safe
    ? `?${RETURN_URL_PARAM}=${encodeURIComponent(safe)}`
    : '';

  if (isSubdomainRoutingEnabled()) {
    return `${toWorkspaceUrl(authPath)}${query}`;
  }
  return `${authPath}${query}`;
}

/** Customer sign-in on the storefront host. */
export function buildCustomerLoginUrl(fromPath?: string): string {
  const path = fromPath ?? getReturnPathForAuthRedirect();
  const safe = sanitizeReturnPath(path);
  const authPath = '/auth';
  const query = safe
    ? `?${RETURN_URL_PARAM}=${encodeURIComponent(safe)}`
    : '';

  if (isSubdomainRoutingEnabled()) {
    return `${toStorefrontUrl(authPath)}${query}`;
  }
  return `${authPath}${query}`;
}

/** Customer login — workspace destinations use staff sign-in instead. */
export function buildLoginUrl(fromPath: string): string {
  if (isWorkspacePath(fromPath)) {
    return buildStaffLoginUrl(fromPath);
  }
  return buildCustomerLoginUrl(fromPath);
}

/** Customer registration URL on `/auth`, preserving optional return path. */
export function buildCustomerRegisterUrl(fromPath?: string): string {
  const path = fromPath ?? getReturnPathForAuthRedirect();
  const params = new URLSearchParams();
  params.set('mode', 'register');
  const safe = sanitizeReturnPath(path);
  if (safe) params.set(RETURN_URL_PARAM, safe);
  const authPath = `/auth?${params.toString()}`;
  if (isSubdomainRoutingEnabled()) {
    return toStorefrontUrl(authPath);
  }
  return authPath;
}

/** @deprecated Use buildStaffLoginUrl for workspace destinations. */
export function buildEmployeeLoginUrl(fromPath?: string): string {
  const path = fromPath ?? getReturnPathForAuthRedirect();
  return buildStaffLoginUrl(path);
}
